import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { handleApiError } from '../lib/api';
import { Mail, Lock, Eye, EyeOff, User, Truck, AlertCircle, Radio, Shield, Sparkles, ArrowRight, CheckCircle } from 'lucide-react';

type Role = 'client' | 'agent';
type Step = 'role' | 'form';

export default function Register() {
  const { registerClient, registerAgent } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('role');
  const [role, setRole] = useState<Role>('client');
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    fullName: '',
    password: '',
    confirmPassword: '',
    vehicleType: 'motorcycle',
    vehiclePlate: '',
    vehicleColor: '',
    licenseNumber: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    if (formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setIsLoading(true);
    try {
      if (role === 'client') {
        await registerClient({
          email: formData.email,
          phone: formData.phone,
          fullName: formData.fullName,
          password: formData.password,
        });
      } else {
        await registerAgent({
          email: formData.email,
          phone: formData.phone,
          fullName: formData.fullName,
          password: formData.password,
          vehicleType: formData.vehicleType as any,
          vehiclePlate: formData.vehiclePlate || undefined,
          vehicleColor: formData.vehicleColor || undefined,
          licenseNumber: formData.licenseNumber || undefined,
        });
      }
      navigate('/');
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const roleOptions = [
    {
      value: 'client' as Role,
      icon: User,
      title: 'Client',
      desc: 'Commander des livraisons et courses',
      features: ['Créer une livraison', 'Suivi temps réel', 'Portefeuille', 'Historique'],
      color: 'bg-blue-100 text-blue-600 border-blue-200',
      iconColor: 'text-blue-600',
    },
    {
      value: 'agent' as Role,
      icon: Truck,
      title: 'Livreur',
      desc: 'Effectuer des livraisons et gagner de l\'argent',
      features: ['Recevoir des courses', 'Gagner 85%/course', 'Portefeuille agent', 'Retraits Mobile Money'],
      color: 'bg-green-100 text-green-600 border-green-200',
      iconColor: 'text-green-600',
    },
  ];

  if (step === 'role') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-secondary/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
        
        <div className="w-full max-w-md relative animate-fade-in">
          <Link to="/" className="inline-flex items-center gap-2 font-bold text-2xl text-primary mb-8" style={{textDecoration: 'none'}}>
            <Shield size={28} />
            OuagaLiv
          </Link>
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-text">Créer un compte</h1>
            <p className="text-text-secondary mt-2">Choisissez votre type de compte pour commencer</p>
          </div>

          <div className="space-y-4 animate-slide-up">
            {roleOptions.map((option, index) => (
              <button
                key={option.value}
                onClick={() => { setRole(option.value); setStep('form'); }}
                className={`btn btn-outline btn-full flex flex-col items-start gap-3 p-5 text-left transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 border-2 ${option.color}`}
                style={{animationDelay: `${index * 100}ms`}}
              >
                <div className="flex items-start gap-4 w-full">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${option.color} ${option.iconColor}`}>
                    <option.icon size={24} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-lg text-text">{option.title}</h3>
                      <CheckCircle size={18} className="text-green-500" />
                    </div>
                    <p className="text-text-secondary text-sm mb-3">{option.desc}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {option.features.map((feat, i) => (
                        <span key={i} className="px-2 py-0.5 bg-white/50 rounded-full text-xs text-text-secondary">{feat}</span>
                      ))}
                    </div>
                  </div>
                  <ArrowRight size={20} className="text-text-secondary group-hover:text-primary transition-colors" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-start justify-center p-4 bg-background pt-6 pb-4">
      <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-secondary/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
      
      <div className="w-full max-w-md relative animate-fade-in">
        <div className="mb-6 animate-fade-in">
          <button onClick={() => setStep('role')} className="text-text-secondary hover:text-text flex items-center gap-1 text-sm">
            <Radio size={18} />
            Changer de type de compte
          </button>
        </div>

        <form onSubmit={handleSubmit} className="card animate-slide-up">
          <div className="mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
              <Shield size={14} />
              {role === 'client' ? 'Inscription Client' : 'Inscription Livreur'}
            </div>
            <h1 className="text-2xl font-bold text-text mt-2">{role === 'client' ? 'Créer mon compte client' : 'Devenir livreur OuagaLiv'}</h1>
            <p className="text-text-secondary mt-1">Remplissez vos informations pour commencer</p>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm animate-slide-up mb-5">
              <AlertCircle size={18} className="flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="space-y-5">
            <div className="input-group">
              <label htmlFor="fullName" className="input-label">Nom complet</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary" size={20} />
                <input
                  id="fullName"
                  type="text"
                  className="input-field pl-12"
                  value={formData.fullName}
                  onChange={e => setFormData({...formData, fullName: e.target.value})}
                  placeholder="Votre nom complet"
                  required
                  autoComplete="name"
                />
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="email" className="input-label">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary" size={20} />
                <input
                  id="email"
                  type="email"
                  className="input-field pl-12"
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                  placeholder="votre@email.com"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="phone" className="input-label">Téléphone</label>
              <input
                id="phone"
                type="tel"
                className="input-field"
                value={formData.phone}
                onChange={e => setFormData({...formData, phone: e.target.value})}
                placeholder="+226 XX XX XX XX"
                required
                autoComplete="tel"
              />
            </div>

            <div className="input-group">
              <label htmlFor="password" className="input-label">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary" size={20} />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className="input-field pl-12 pr-12"
                  value={formData.password}
                  onChange={e => setFormData({...formData, password: e.target.value})}
                  placeholder="•••••••• (min 8 caractères)"
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary hover:text-primary transition-colors"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="confirmPassword" className="input-label">Confirmer le mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary" size={20} />
                <input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  className="input-field pl-12"
                  value={formData.confirmPassword}
                  onChange={e => setFormData({...formData, confirmPassword: e.target.value})}
                  placeholder="••••••••"
                  required
                  autoComplete="new-password"
                />
              </div>
            </div>

            {role === 'agent' && (
              <div className="border-t border-border pt-6 mt-4 space-y-5 animate-fade-in">
                <div className="flex items-center gap-2">
                  <Truck size={20} className="text-secondary" />
                  <h3 className="font-semibold text-text">Informations véhicule</h3>
                </div>
                <div className="input-group">
                  <label className="input-label">Type de véhicule</label>
                  <select
                    className="input-field"
                    value={formData.vehicleType}
                    onChange={e => setFormData({...formData, vehicleType: e.target.value})}
                  >
                    <option value="motorcycle">🛵 Moto</option>
                    <option value="bicycle">🚲 Vélo</option>
                    <option value="car">🚗 Voiture</option>
                    <option value="tricycle">🛺 Tricycle</option>
                    <option value="foot">👟 À pied</option>
                  </select>
                </div>
                <div className="input-group">
                  <label htmlFor="vehiclePlate" className="input-label">Plaque d'immatriculation (optionnel)</label>
                  <input
                    id="vehiclePlate"
                    type="text"
                    className="input-field"
                    value={formData.vehiclePlate}
                    onChange={e => setFormData({...formData, vehiclePlate: e.target.value})}
                    placeholder="BF-1234-AB"
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="vehicleColor" className="input-label">Couleur du véhicule (optionnel)</label>
                  <input
                    id="vehicleColor"
                    type="text"
                    className="input-field"
                    value={formData.vehicleColor}
                    onChange={e => setFormData({...formData, vehicleColor: e.target.value})}
                    placeholder="Noir, Blanc, Rouge..."
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="licenseNumber" className="input-label">Numéro de permis (optionnel)</label>
                  <input
                    id="licenseNumber"
                    type="text"
                    className="input-field"
                    value={formData.licenseNumber}
                    onChange={e => setFormData({...formData, licenseNumber: e.target.value})}
                    placeholder="PERMIS-12345"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg border border-primary/10 mt-2">
              <Shield size={16} className="text-primary" />
              <span className="text-sm text-primary">Vos données sont sécurisées et ne seront jamais partagées</span>
            </div>

            <button type="submit" className="btn btn-primary btn-full mt-2" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="loading-spinner" />
                  Inscription...
                </>
              ) : (
                role === 'client' ? 'Créer mon compte client' : 'Devenir livreur'
              )}
            </button>
          </div>
        </form>

        <div className="text-center mt-6 animate-fade-in">
          <p className="text-text-secondary">
            Déjà un compte ? <Link to="/login" className="text-primary font-semibold hover:underline">Se connecter</Link>
          </p>
        </div>

        {/* Demo credentials */}
        <div className="mt-6 p-4 bg-primary/5 rounded-xl border border-primary/10 animate-fade-in">
          <p className="text-xs text-primary font-medium mb-2">Comptes de test :</p>
          <div className="text-xs text-text-secondary space-y-1 font-mono">
            <div>Admin: admin@ouagaliv.bf / admin123</div>
            <div>Client: client@test.bf / client123</div>
            <div>Agent: agent1@test.bf / agent123</div>
          </div>
        </div>
      </div>
    </div>
  );
}